'use client';

import { c as _c } from "react/compiler-runtime";
import Row from "@/components/Layout/Flex/Row";
import { chainSettings, fallbackChainId, type ChainId } from "@/constants/chain-specific";
import type { RequiredFarmChainType } from "@/constants/project-specific";
import { FARM_PAGE_CONFIG } from "@/constants/project-specific/farms";
import type { ChainFarmData, FarmConfig, FarmRewardData, FarmRewardPrices, NativeTokenPrices } from "@/constants/project-specific/farms/types";
import React from "react";
import FarmCard from "./FarmCard";
import { Farms } from '@/constants/project-specific/farms/types';
import { getTotalFarmsTVLPerChain } from "@/lib/farms/helpers";
import { Skeleton } from "@heroui/react";
import { usePonderFarmData } from "@/hooks/farms/usePonderFarmData";
import { useFarmPrices } from "@/hooks/farms/useFarmPrices";
interface FarmOverviewProps {
  farms?: ChainFarmData;
  prices: FarmRewardPrices;
  nativePrices: NativeTokenPrices;
  currChain?: RequiredFarmChainType;
  defaultTokenPrice?: FarmRewardData;
}
export default function FarmOverview(t0) {
  const $ = _c(64);
  const {
    prices,
    nativePrices,
    currChain,
    defaultTokenPrice
  } = t0;
  const farmChains = FARM_PAGE_CONFIG.chainIds;
  let t1;
  if ($[0] !== nativePrices || $[1] !== prices) {
    t1 = {
      initialPrices: prices,
      initialNativePrices: nativePrices,
      enableClientFallback: true
    };
    $[0] = nativePrices;
    $[1] = prices;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    prices: finalPrices,
    nativePrices: finalNativePrices,
    loading: pricesLoading,
    isUsingFallback: pricesFallback,
    error: pricesError
  } = useFarmPrices(t1);
  let t2;
  if ($[3] !== currChain) {
    t2 = currChain ? [currChain] : undefined;
    $[3] = currChain;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: farms,
    loading: farmsLoading,
    error: farmsError,
    usedFallback: farmsFallback
  } = usePonderFarmData(finalPrices, finalNativePrices, t2);
  const loading = pricesLoading || farmsLoading;
  const error = pricesError || farmsError;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== currChain || $[6] !== defaultTokenPrice?.rewardPrice || $[7] !== error || $[8] !== farms || $[9] !== farmsFallback || $[10] !== finalNativePrices || $[11] !== loading || $[12] !== pricesFallback) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const tvlPerChain = farms ? getTotalFarmsTVLPerChain(farms) ?? {} : {};
      if (loading) {
        let t11;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <Row className="justify-evenly self-center gap-x-2 gap-y-6 flex-wrap w-full md:w-3/4 pb-5">{farmChains.map(_temp)}</Row>;
          $[21] = t11;
        } else {
          t11 = $[21];
        }
        t10 = t11;
        break bb0;
      }
      if (error) {
        let t11;
        let t12;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <h2 className="text-xl font-bold text-red-500 mb-2">Failed to Fetch Farm Data!</h2>;
          t12 = <p className="text-gray-400 mb-4">Unable to load farm information. Please try again later.</p>;
          $[22] = t11;
          $[23] = t12;
        } else {
          t11 = $[22];
          t12 = $[23];
        }
        let t13;
        if ($[24] !== error) {
          t13 = <p className="text-sm text-gray-500">Error: {error}</p>;
          $[24] = error;
          $[25] = t13;
        } else {
          t13 = $[25];
        }
        let t14;
        if ($[26] !== farmsFallback) {
          t14 = farmsFallback && <p className="text-xs text-yellow-500 mt-2">⚠️ Ponder API unavailable, client-side fallback also failed</p>;
          $[26] = farmsFallback;
          $[27] = t14;
        } else {
          t14 = $[27];
        }
        let t15;
        if ($[28] !== pricesFallback) {
          t15 = pricesFallback && <p className="text-xs text-orange-500 mt-1">⚠️ Using client-side price data fetching</p>;
          $[28] = pricesFallback;
          $[29] = t15;
        } else {
          t15 = $[29];
        }
        let t16;
        if ($[30] !== t13 || $[31] !== t14 || $[32] !== t15) {
          t16 = <div className="flex flex-col items-center justify-center p-8 text-center">{t11}{t12}{t13}{t14}{t15}</div>;
          $[30] = t13;
          $[31] = t14;
          $[32] = t15;
          $[33] = t16;
        } else {
          t16 = $[33];
        }
        t10 = t16;
        break bb0;
      }
      if (!farms) {
        let t11;
        if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <div className="flex flex-col items-center justify-center p-8 text-center"><p className="text-gray-400">No farm data available</p></div>;
          $[34] = t11;
        } else {
          t11 = $[34];
        }
        t10 = t11;
        break bb0;
      }
      let t11;
      if ($[35] !== farms) {
        t11 = farmChains.map(farmChain_0 => <React.Fragment key={farmChain_0}>{farms[farmChain_0 as keyof typeof farms].map((farmConfig, index_0) => {
            if (farmConfig.display !== false) {
              return <FarmCard key={`${farmConfig.masterChefSlug}-${farmConfig.pid}-card`} farm={{
                ...farmConfig,
                chainId: farmChain_0 as RequiredFarmChainType
              }} index={index_0} chain={farmChain_0} />;
            }
          })}</React.Fragment>);
        $[35] = farms;
        $[36] = t11;
      } else {
        t11 = $[36];
      }
      if ($[37] !== t11) {
        t9 = <Row className="justify-evenly self-center gap-x-2 gap-y-6 flex-wrap w-full md:w-3/4 pb-5">{t11}</Row>;
        $[37] = t11;
        $[38] = t9;
      } else {
        t9 = $[38];
      }
      t8 = "flex w-full md:w-1/2 mt-5 bg-black/85 rounded-t-xl place-self-center place-content-center";
      t5 = "flex flex-col flex-wrap justify-items-center justify-center items-center gap-2 md:flex-row md:justify-evenly p-2 w-full";
      const t12 = FARM_PAGE_CONFIG.defaultChef[currChain ?? fallbackChainId];
      const t13 = Number(defaultTokenPrice?.rewardPrice);
      let t14;
      if ($[39] !== currChain || $[40] !== t13) {
        t14 = t13.toFixed(FARM_PAGE_CONFIG.defaultChef[currChain ?? fallbackChainId].rewardToken.priceDecimals || 4);
        $[39] = currChain;
        $[40] = t13;
        $[41] = t14;
      } else {
        t14 = $[41];
      }
      if ($[42] !== t12.rewardToken.symbol || $[43] !== t14) {
        t6 = <div>{t12.rewardToken.symbol} Price: ${t14}</div>;
        $[42] = t12.rewardToken.symbol;
        $[43] = t14;
        $[44] = t6;
      } else {
        t6 = $[44];
      }
      const t15 = chainSettings[currChain ?? fallbackChainId];
      const t16 = Number(finalNativePrices[currChain ?? fallbackChainId]?.priceUsd || 0);
      let t17;
      if ($[45] !== t16) {
        t17 = t16.toFixed(2);
        $[45] = t16;
        $[46] = t17;
      } else {
        t17 = $[46];
      }
      if ($[47] !== t15.nativeCurrency.symbol || $[48] !== t17) {
        t7 = <div>{t15.nativeCurrency.symbol}{" "}Price: ${t17}</div>;
        $[47] = t15.nativeCurrency.symbol;
        $[48] = t17;
        $[49] = t7;
      } else {
        t7 = $[49];
      }
      t3 = "Farmed TVL: $";
      t4 = tvlPerChain[currChain ?? fallbackChainId].toLocaleString();
    }
    $[5] = currChain;
    $[6] = defaultTokenPrice?.rewardPrice;
    $[7] = error;
    $[8] = farms;
    $[9] = farmsFallback;
    $[10] = finalNativePrices;
    $[11] = loading;
    $[12] = pricesFallback;
    $[13] = t10;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t10 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[50] !== t3 || $[51] !== t4) {
    t11 = <div>{t3}{t4}</div>;
    $[50] = t3;
    $[51] = t4;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== t11 || $[54] !== t5 || $[55] !== t6 || $[56] !== t7) {
    t12 = <div className={t5}>{t6}{t7}{t11}</div>;
    $[53] = t11;
    $[54] = t5;
    $[55] = t6;
    $[56] = t7;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  if ($[58] !== t12 || $[59] !== t8) {
    t13 = <div className={t8}>{t12}</div>;
    $[58] = t12;
    $[59] = t8;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== t13 || $[62] !== t9) {
    t14 = <>{t9}{t13}</>;
    $[61] = t13;
    $[62] = t9;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  return t14;
}
function _temp(farmChain) {
  return Array.from({
    length: 3
  }, (_, index) => <Skeleton key={`skeleton-${farmChain}-${index}`} className="w-[300px] h-[365px] rounded-xl" />);
}